import {
  Box,
  Button,
  Dialog,
  DialogProps,
  DialogActions,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Radio,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  FormLabel
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState } from 'react';
import { styles } from './DeleteItemsDialog.styles';
import { Folder } from '../../types';
import { LocationSelect } from '../LocationSelect/LocationSelect';
import { useParentFolder } from '../LocationSelect/hooks/useParentFolder';

const intl = {
  title: 'Delete Items',
  label: 'Type "DELETE" to confirm',
  confirmString: 'DELETE',
  cancel: 'Cancel',
  delete: 'Delete',
  closeIconAriaLabel: 'close button for dialog',
  folderContentActionsLabel: (count: number) =>
    `What would you like to do with the content of the ${count === 1 ? 'folder' : 'folders'}?`,
  folderContentActions: {
    move: 'Move it to another folder',
    delete: 'Delete it'
  },
  locationSelectLabel: 'Which folder do you like to move the content to?'
};

type DeleteItemsDialogProps = DialogProps & {
  title?: string;
  folderToDeleteIds: Folder['id'][];
  showFolderContentActions?: boolean;
  isDeleting?: boolean;
  onClose: () => void;
  onDelete: (parentFolderId?: Folder['id']) => void;
};

export function DeleteItemsDialog({
  title = intl.title,
  showFolderContentActions = false,
  folderToDeleteIds,
  isDeleting = false,
  onDelete,
  ...dialogProps
}: DeleteItemsDialogProps) {
  const { parentFolder, subfolders, setParentFolderId } = useParentFolder();
  const [folderContentAction, setFolderContentAction] = useState<'move' | 'delete'>('move');

  const [confirmation, setConfirmation] = useState('');
  const isDisabled = confirmation !== intl.confirmString;

  const handleDelete = () => {
    onDelete(showFolderContentActions && folderContentAction === 'move' ? parentFolder.id : undefined);
  };

  return (
    <Dialog fullWidth {...dialogProps}>
      <Box sx={styles.header}>
        <DialogTitle sx={styles.title}>
          <ErrorIcon />
          {title}
        </DialogTitle>
        <IconButton sx={styles.closeIcon} aria-label={intl.closeIconAriaLabel} onClick={dialogProps.onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={styles.content}>
        {dialogProps.children}

        {showFolderContentActions ? (
          <Box sx={styles.folderContentActionsContainer}>
            <FormControl>
              <FormLabel sx={styles.folderContentActionsLabel} id="folder-content-actions-label">
                {intl.folderContentActionsLabel(folderToDeleteIds.length || 0)}
              </FormLabel>
              <RadioGroup
                aria-labelledby="folder-content-actions-label"
                defaultValue="move"
                name="folder-content-actions"
                value={folderContentAction}
                onChange={(e) => {
                  setFolderContentAction(e.target.value === 'move' ? 'move' : 'delete');
                }}
              >
                <FormControlLabel
                  value="move"
                  control={<Radio size="medium" />}
                  label={<Typography component="span">{intl.folderContentActions.move}</Typography>}
                />
                <FormControlLabel
                  value="delete"
                  control={<Radio size="medium" />}
                  label={<Typography component="span">{intl.folderContentActions.delete}</Typography>}
                />
              </RadioGroup>
            </FormControl>

            {folderContentAction === 'move' ? (
              <LocationSelect
                label={intl.locationSelectLabel}
                withCreateNewFolder={true}
                disabledFolderIds={folderToDeleteIds}
                selectedFolder={parentFolder}
                subfolders={subfolders}
                onFolderSelect={setParentFolderId}
              />
            ) : null}
          </Box>
        ) : null}

        <TextField
          fullWidth={true}
          label={intl.label}
          value={confirmation}
          onChange={(event) => {
            setConfirmation(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !isDisabled) {
              handleDelete();
            }
          }}
          inputProps={{
            className: 'no-format',
            'data-test': 'confirmationInput'
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button disabled={isDeleting} color="secondary" data-test="cancelDeleteButton" onClick={dialogProps.onClose}>
          {intl.cancel}
        </Button>
        <LoadingButton
          loading={isDeleting}
          disabled={isDisabled}
          color="error"
          variant="contained"
          data-test="deleteButton"
          onClick={handleDelete}
        >
          {intl.delete}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
